import { useMemo } from 'react';
import useDevice from './useDevice';

enum SUPPORTED_IMAGE_FORMATS {
  'jpg' = 'jpg',
  'webp' = 'webp',
  'avif' = 'avif',
}

type ValidCatalogImageFormats = keyof typeof SUPPORTED_IMAGE_FORMATS;

const getMajorVersion = (version: string): number =>
  parseInt(version.split('.')?.[0] ?? '0', 10);

/**
 *
 * @param browserName
 * @param browserMajorVersion
 * @param osName
 */
const canUseAvif = (
  browserName: string,
  browserMajorVersion: number,
  osName: string,
): boolean => {
  // Chrome
  if (
    browserName === 'Chrome' &&
    browserMajorVersion > 90 &&
    osName !== 'iOS'
  ) {
    return true;
  }

  // Firefox
  if (
    browserName === 'Firefox' &&
    browserMajorVersion > 93 &&
    osName !== 'iOS'
  ) {
    return true;
  }

  // Opera
  if (browserName === 'Opera' && browserMajorVersion > 71 && osName !== 'iOS') {
    return true;
  }

  return false;
};

const canUseWebp = (
  browserName: string,
  browserMajorVersion: number,
  osName: string,
  osMajorVersion: number,
  deviceType: string,
) => {
  // Firefox
  if (
    browserName === 'Firefox' &&
    browserMajorVersion > 65 &&
    browserMajorVersion < 93
  ) {
    return SUPPORTED_IMAGE_FORMATS.webp;
  }

  // Safari 14
  if (
    browserName === 'Safari' &&
    browserMajorVersion >= 14 &&
    deviceType !== 'desktop'
  ) {
    return SUPPORTED_IMAGE_FORMATS.webp;
  }

  if (
    browserName === 'Chrome' &&
    browserMajorVersion > 33 &&
    osName !== 'iOS'
  ) {
    return true;
  }

  // Chrome on IOS
  if (
    browserName === 'Chrome' &&
    deviceType !== 'desktop' &&
    osName === 'iOS' &&
    osMajorVersion >= 14
  ) {
    return SUPPORTED_IMAGE_FORMATS.webp;
  }

  // Safari 15 on desktop (due to big sur limits and bad device detection)
  if (
    browserName === 'Safari' &&
    browserMajorVersion >= 15 &&
    deviceType === 'desktop'
  ) {
    return SUPPORTED_IMAGE_FORMATS.webp;
  }

  // Edge
  if (browserName === 'Microsoft Edge' && browserMajorVersion >= 18) {
    return SUPPORTED_IMAGE_FORMATS.webp;
  }
};

/**
 * General preference:
 * 1) avif
 * 2) webp
 * 3) Jpg
 *
 *  References:
 *  @see https://bugs.webkit.org/show_bug.cgi?id=213622 (don't use mac desktop version checking)
 *  @see https://caniuse.com/avif
 *  @see https://caniuse.com/webp
 *
 */
const useDevicePreferredImageFormat = ({
  disableAvif = false,
}: { disableAvif?: boolean } = {}): ValidCatalogImageFormats => {
  const deviceInfo = useDevice();

  return useMemo((): ValidCatalogImageFormats => {
    if (!deviceInfo) {
      return SUPPORTED_IMAGE_FORMATS.jpg;
    }

    const browserName = deviceInfo.browser.name;
    const browserMajorVersion = getMajorVersion(
      deviceInfo.browser.version ?? '',
    );

    const osName = deviceInfo.os.name;
    const osMajorVersion = getMajorVersion(deviceInfo.os.version ?? '');
    const deviceType = deviceInfo.platform.type;

    if (!browserName || !osName || !deviceType) {
      return SUPPORTED_IMAGE_FORMATS.webp;
    }

    //
    // AVIF
    if (!disableAvif && canUseAvif(browserName, browserMajorVersion, osName)) {
      return SUPPORTED_IMAGE_FORMATS.avif;
    }

    //
    // WEBP
    if (
      canUseWebp(
        browserName,
        browserMajorVersion,
        osName,
        osMajorVersion,
        deviceType,
      )
    ) {
      return SUPPORTED_IMAGE_FORMATS.webp;
    }

    return SUPPORTED_IMAGE_FORMATS.jpg;
  }, [deviceInfo, disableAvif]);
};

export default useDevicePreferredImageFormat;
